import { useEffect, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  useSku,
  useSkuUsage,
  useUpdateSku,
  useCreateSku,
  useDeleteSku,
  useReplaceSkuBom,
  useReplaceSkuSuppliers,
} from '../../hooks/useWmsSkus';
import { SkuOverviewTab } from './tabs/SkuOverviewTab';
import { SkuBomTab } from './tabs/SkuBomTab';
import { SkuSuppliersTab } from './tabs/SkuSuppliersTab';
import { SkuPhotosTab } from './tabs/SkuPhotosTab';
import { SkuDocumentsTab } from './tabs/SkuDocumentsTab';
import { SkuAccountingTab } from './tabs/SkuAccountingTab';
import type { CreateSkuInput, UpdateSkuInput, SkuSupplier } from '../../types';

type EditableSupplier = Omit<SkuSupplier, 'id'> & { id?: string };

type PanelTab = 'overview' | 'bom' | 'suppliers' | 'photos' | 'documents' | 'accounting';

const TABS: { value: PanelTab; label: string; createDisabled?: boolean }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'bom', label: 'BOM', createDisabled: true },
  { value: 'suppliers', label: 'Suppliers', createDisabled: true },
  { value: 'photos', label: 'Photos', createDisabled: true },
  { value: 'documents', label: 'Documents', createDisabled: true },
  { value: 'accounting', label: 'Accounting', createDisabled: true },
];

const DEFAULT_CREATE_DRAFT: Partial<CreateSkuInput> = {
  type: 'inventory',
  activated: true,
  saleable: true,
};

interface SkuDetailPanelProps {
  skuId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

export function SkuDetailPanel({
  skuId,
  mode,
  open,
  onOpenChange,
  onCreated,
}: SkuDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('overview');
  const [createDraft, setCreateDraft] = useState<Partial<CreateSkuInput>>(
    DEFAULT_CREATE_DRAFT,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: sku } = useSku(mode === 'view' ? skuId ?? undefined : undefined);
  const { data: usage } = useSkuUsage(
    mode === 'view' && confirmDelete ? skuId ?? undefined : undefined,
  );
  const updateMutation = useUpdateSku();
  const createMutation = useCreateSku();
  const deleteMutation = useDeleteSku();
  const replaceBomMutation = useReplaceSkuBom();
  const replaceSuppliersMutation = useReplaceSkuSuppliers();

  useEffect(() => {
    setActiveTab('overview');
    if (mode === 'create') setCreateDraft(DEFAULT_CREATE_DRAFT);
  }, [skuId, mode, open]);

  const handleOverviewSave = (patch: Partial<UpdateSkuInput> | Partial<CreateSkuInput>) => {
    if (mode === 'create') {
      setCreateDraft((prev) => ({ ...prev, ...patch }));
      return;
    }
    if (!sku) return;
    updateMutation.mutate({ id: sku.id, data: patch as UpdateSkuInput });
  };

  const handleAccountingSave = (patch: Partial<UpdateSkuInput>) => {
    if (mode !== 'view' || !sku) return;
    updateMutation.mutate({ id: sku.id, data: patch });
  };

  const handleBomSave = (
    items: Array<{ componentSkuId: string; qty: number }>,
  ) => {
    if (!sku) return;
    replaceBomMutation.mutate({
      id: sku.id,
      items: items.map((i) => ({ component_sku_id: i.componentSkuId, qty: i.qty })),
    });
  };

  const handleSuppliersSave = (suppliers: EditableSupplier[]) => {
    if (!sku) return;
    replaceSuppliersMutation.mutate({
      id: sku.id,
      suppliers: suppliers.map((s) => ({
        organisation_id: s.organisationId || '',
        organisation_name: s.organisationName,
        supplier_sku: s.supplierSku,
        link: s.link,
        price: s.price ? parseFloat(s.price) : undefined,
        lead_time_days: s.leadTimeDays,
        moq: s.moq,
      })),
    });
  };

  const handleDelete = async () => {
    if (!sku) return;
    if (usage?.isUsed) {
      toast.error('Cannot delete SKU', {
        description: 'Remove it from inventory and BOMs first.',
      });
      return;
    }
    await deleteMutation.mutateAsync(sku.id);
    setConfirmDelete(false);
    onOpenChange(false);
  };

  const handleCreateSubmit = async () => {
    if (
      !createDraft.sku ||
      !createDraft.nameEn ||
      !createDraft.nameFr ||
      !createDraft.type
    ) {
      toast.error('SKU code, type, English name, and French name are required');
      return;
    }
    const created = await createMutation.mutateAsync(createDraft as CreateSkuInput);
    onCreated?.(created.id);
  };

  if (!open) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-sm truncate">
            {mode === 'create' ? 'New SKU' : sku?.sku ?? '...'}
          </span>
          {mode === 'view' && sku && (
            <>
              <Badge variant={sku.activated ? 'default' : 'secondary'}>
                {sku.activated ? 'Active' : 'Inactive'}
              </Badge>
              {sku.saleable && <Badge variant="outline">Saleable</Badge>}
            </>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {mode === 'view' && sku && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setConfirmDelete(true)}
              title="Delete SKU"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Inner tab bar */}
      <div className="flex items-center gap-0 px-2 border-b">
        {TABS.map((t) => {
          const disabled = mode === 'create' && t.createDisabled;
          return (
            <button
              key={t.value}
              type="button"
              disabled={disabled}
              onClick={() => setActiveTab(t.value)}
              title={disabled ? 'Save the SKU first to add this' : undefined}
              className={cn(
                'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === t.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                disabled && 'opacity-40 cursor-not-allowed',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'overview' && (
          <SkuOverviewTab
            sku={mode === 'view' ? sku ?? null : null}
            mode={mode}
            onSave={handleOverviewSave}
          />
        )}
        {mode === 'view' && sku && activeTab === 'bom' && (
          <SkuBomTab sku={sku} onSave={handleBomSave} />
        )}
        {mode === 'view' && sku && activeTab === 'suppliers' && (
          <SkuSuppliersTab sku={sku} onSave={handleSuppliersSave} />
        )}
        {mode === 'view' && sku && activeTab === 'photos' && <SkuPhotosTab sku={sku} />}
        {mode === 'view' && sku && activeTab === 'documents' && <SkuDocumentsTab sku={sku} />}
        {mode === 'view' && sku && activeTab === 'accounting' && (
          <SkuAccountingTab sku={sku} onSave={handleAccountingSave} />
        )}
      </div>

      {/* Create-mode footer CTA */}
      {mode === 'create' && (
        <div className="border-t px-4 py-3 flex justify-end">
          <Button onClick={handleCreateSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create SKU'}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SKU?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This will soft-delete <strong className="font-mono">{sku?.sku}</strong>.
                  It will no longer appear in the catalog but can be restored from the database.
                </p>
                {usage?.isUsed && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    <div className="font-medium">Cannot delete — still in use:</div>
                    <ul className="mt-1 list-disc list-inside">
                      {usage.usedIn.map((u) => (
                        <li key={u.type}>
                          {u.count} × {u.type.replace(/_/g, ' ')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending || usage?.isUsed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
